// packages/web/src/lib/store/account-slice.ts
/**
 * Account slice — user's point balance + credits history + subscription +
 * invite + notify preferences.
 *
 * 19BN Phase 2: balance is the user-facing proxy for LLM cost. The avatar and
 * sidebar display it; the preflight gate reads it before AI calls. refreshBalance
 * is called after login (useAuthInit) and after each generation completes.
 *
 * 19BN Phase D D1: extended with subscription / invite / notifyChannels state
 * + the actions that back the AccountDrawer's subscription + settings tabs.
 * refreshBalance now persists notifyChannels (it was already in the API
 * response but the slice dropped it).
 *
 * Design: separate from auth-slice because balance changes frequently
 * (every generation) while auth state changes rarely (login/logout). Keeping
 * them separate avoids re-rendering auth-dependent components on every deduction.
 */

import type { StateCreator } from 'zustand';
import type { AppStore } from './index.js';
import type { SubscriptionState } from '../../api/subscription.js';
// 19BO+ B5.2-cron: import interval-hours 受控枚举 + 校验，用于把云端 number
// 安全 narrowing 成 DiagnoseIntervalHours 再写进 diagnose-slice。types-only
// + const import 不构成 slice creator 循环依赖。
import { DIAGNOSE_INTERVAL_VALUES, type DiagnoseIntervalHours } from './diagnose-slice.js';

/** 把云端 number 安全转成 DiagnoseIntervalHours。非法值降级 6h（对齐后端 DEFAULT）。 */
function coerceIntervalHours(v: number): DiagnoseIntervalHours {
  return (DIAGNOSE_INTERVAL_VALUES as readonly number[]).includes(v) ? (v as DiagnoseIntervalHours) : 6;
}

/** Notify channel switches. inApp is always on (spec §4.3 — not user-toggleable). */
export interface NotifyChannels {
  inApp: true;
  email: boolean;
  push: boolean;
}

export interface InviteSummary {
  inviteCode: string;
  invitedBy: string | null;
  quotaUsed: number;
  quotaTotal: number;
}

export interface AccountSlice {
  // ── State ──────────────────────────────────────────────────────────────
  /** Current point balance (null = not loaded / anonymous). */
  balance: number | null;
  /** True while balance is loading (initial fetch after login). */
  balanceLoading: boolean;
  /** User's low-balance alert threshold. */
  lowBalanceThreshold: number;
  /** Notify channel switches (backed by /account/balance + /account/preferences). */
  notifyChannels: NotifyChannels;
  /** 19BO+ Line 2 B4.4 step 4: progress-cloud-sync opt-in (accounts-level pref).
   *  When true, learning-progress field-level events (wrongAnswers/srs/...) are
   *  diffed + enqueued for cloud upload via the progressSyncHook. Default false. */
  progressCloudSync: boolean;
  /** 19BO+ B5.2-cron: auto-diagnose opt-in (accounts-level pref · cloud-synced).
   *  控制后端 cron 是否代用户跑 LLM 诊断。与 progressCloudSync 是双重 opt-in
   *  ——两者都为 true cron 才诊断。这是云端权威值；diagnose-slice.diagnosePreferences
   *  镜像它（localStorage 作离线兜底）。refreshBalance 后同步进 diagnose-slice。 */
  autoDiagnoseEnabled: boolean;
  /** 19BO+ B5.2-cron: cron 诊断间隔（小时），受控枚举 6/12/24。云端权威值。 */
  autoDiagnoseIntervalHours: number;
  /** Credits history (most recent first). Lazy-loaded by the credits page. */
  creditsHistory: Array<{
    id: string;
    delta: number;
    balanceAfter: number;
    reason: string;
    refId: string | null;
    createdAt: number;
  }>;
  creditsHistoryTotal: number;
  /** Current subscription (null = not loaded yet; {active:false} = loaded, none). */
  subscription: SubscriptionState | null;
  /** Invite summary (null = not loaded yet). */
  invite: InviteSummary | null;

  // ── Actions ────────────────────────────────────────────────────────────
  /** Fetch balance + threshold + notifyChannels from /account/balance. No-op for anonymous. */
  refreshBalance: () => Promise<void>;
  /** Fetch credits history page. */
  loadCreditsHistory: (page?: number) => Promise<void>;
  /** Fetch the active subscription. Null response leaves state untouched. */
  refreshSubscription: () => Promise<void>;
  /** Fetch the invite summary (code + quota). Null response leaves state untouched. */
  refreshInvite: () => Promise<void>;
  /** Update notify channels server-side then locally. Reverts on failure. */
  updateNotifyChannels: (patch: Partial<Pick<NotifyChannels, 'email' | 'push'>>) => Promise<void>;
  /** Update the low-balance threshold server-side then locally. Reverts on failure. */
  updateThreshold: (threshold: number) => Promise<void>;
  /** 19BO+ Line 2 B4.4 step 4: toggle progress-cloud-sync opt-in server-side then locally. */
  updateProgressCloudSync: (enabled: boolean) => Promise<void>;
  /** 19BO+ B5.2-cron: toggle auto-diagnose opt-in server-side then locally + 同步进 diagnose-slice. */
  updateAutoDiagnoseEnabled: (enabled: boolean) => Promise<void>;
  /** 19BO+ B5.2-cron: update cron 诊断间隔 server-side then locally + 同步进 diagnose-slice. */
  updateAutoDiagnoseInterval: (intervalHours: DiagnoseIntervalHours) => Promise<void>;
  /** Optimistically deduct points locally (after a generation fires). */
  deductLocally: (points: number) => void;
  /** Clear account state (on logout). */
  clearAccount: () => void;
}

export const createAccountSlice: StateCreator<AppStore, [], [], AccountSlice> = (set, get) => ({
  balance: null,
  balanceLoading: false,
  lowBalanceThreshold: 100,
  notifyChannels: { inApp: true, email: true, push: false },
  progressCloudSync: false,
  // 19BO+ B5.2-cron: defaults mirror backend (enabled=off, interval=6h).
  autoDiagnoseEnabled: false,
  autoDiagnoseIntervalHours: 6,
  creditsHistory: [],
  creditsHistoryTotal: 0,
  subscription: null,
  invite: null,

  refreshBalance: async () => {
    // Only fetch for authenticated users.
    const user = get().currentUser;
    if (!user || user.isAnonymous) {
      set({ balance: null, balanceLoading: false });
      return;
    }
    set({ balanceLoading: true });
    try {
      const { accountApi } = await import('../../api/account.js');
      const result = await accountApi.getBalance();
      if (result) {
        set({
          balance: result.balance,
          lowBalanceThreshold: result.lowBalanceThreshold,
          // D1: persist the notify channel switches that were previously dropped.
          notifyChannels: {
            inApp: true,
            email: result.notifyEmail,
            push: result.notifyPush,
          },
          // 19BO+ Line 2 B4.4 step 4: hydrate progress-cloud-sync opt-in.
          progressCloudSync: result.progressCloudSync,
          // 19BO+ B5.2-cron: hydrate auto-diagnose prefs (云端权威).
          autoDiagnoseEnabled: result.autoDiagnoseEnabled,
          autoDiagnoseIntervalHours: result.autoDiagnoseIntervalHours,
          balanceLoading: false,
        });
        // 19BO+ B5.2-cron: 同步云端值进 diagnose-slice.diagnosePreferences，
        // 让 use-auto-diagnose / settings UI 立即看到跨设备一致的偏好。
        // diagnose-slice 的 localStorage 是离线兜底；登录后以云端为准。
        const intervalHours = coerceIntervalHours(result.autoDiagnoseIntervalHours);
        set({
          diagnosePreferences: {
            autoDiagnoseEnabled: result.autoDiagnoseEnabled,
            autoDiagnoseIntervalHours: intervalHours,
          },
        });
      } else {
        // 401 — session expired. Clear balance.
        set({ balance: null, balanceLoading: false });
      }
    } catch {
      // Network error — keep stale balance, stop loading.
      set({ balanceLoading: false });
    }
  },

  loadCreditsHistory: async (page = 1) => {
    try {
      const { accountApi } = await import('../../api/account.js');
      const result = await accountApi.getCreditsHistory(page);
      if (result) {
        set({ creditsHistory: result.items, creditsHistoryTotal: result.total });
      }
    } catch {
      // best-effort — credits history is non-critical
    }
  },

  refreshSubscription: async () => {
    const user = get().currentUser;
    if (!user || user.isAnonymous) return;
    try {
      const { subscriptionApi } = await import('../../api/subscription.js');
      const result = await subscriptionApi.get();
      // 401 → null: leave existing state alone (don't wipe a loaded sub).
      if (result) set({ subscription: result });
    } catch {
      // non-critical
    }
  },

  refreshInvite: async () => {
    const user = get().currentUser;
    if (!user || user.isAnonymous) return;
    try {
      const { inviteApi } = await import('../../api/invite.js');
      const result = await inviteApi.get();
      if (result) {
        set({
          invite: {
            inviteCode: result.inviteCode,
            invitedBy: result.invitedBy,
            quotaUsed: result.quotaUsed,
            quotaTotal: result.quotaTotal,
          },
        });
      }
    } catch {
      // non-critical
    }
  },

  updateNotifyChannels: async (patch) => {
    const prev = get().notifyChannels;
    const next = { ...prev, ...patch };
    // Optimistic update — revert if the server rejects.
    set({ notifyChannels: next });
    try {
      const { accountApi } = await import('../../api/account.js');
      // Map slice's {email,push} keys to the backend's {notifyEmail,notifyPush}.
      const pref: { notifyEmail?: boolean; notifyPush?: boolean } = {};
      if (typeof patch.email === 'boolean') pref.notifyEmail = patch.email;
      if (typeof patch.push === 'boolean') pref.notifyPush = patch.push;
      await accountApi.updatePreferences(pref);
    } catch (err) {
      set({ notifyChannels: prev });
      throw err;
    }
  },

  updateThreshold: async (threshold) => {
    const prev = get().lowBalanceThreshold;
    set({ lowBalanceThreshold: threshold });
    try {
      const { accountApi } = await import('../../api/account.js');
      await accountApi.updatePreferences({ lowBalanceThreshold: threshold });
    } catch (err) {
      set({ lowBalanceThreshold: prev });
      throw err;
    }
  },

  updateProgressCloudSync: async (enabled) => {
    const prev = get().progressCloudSync;
    set({ progressCloudSync: enabled });
    try {
      const { accountApi } = await import('../../api/account.js');
      await accountApi.updatePreferences({ progressCloudSync: enabled });
    } catch (err) {
      set({ progressCloudSync: prev });
      throw err;
    }
  },

  // 19BO+ B5.2-cron: auto-diagnose prefs 双 slice 同步。
  // optimistic 写 account-slice（云端权威）+ diagnose-slice（消费源），
  // 失败回滚两者。上报 accountApi 让后端 cron work-list 看到新值。
  updateAutoDiagnoseEnabled: async (enabled) => {
    const prevEnabled = get().autoDiagnoseEnabled;
    const prevPrefs = get().diagnosePreferences;
    set({
      autoDiagnoseEnabled: enabled,
      diagnosePreferences: { ...prevPrefs, autoDiagnoseEnabled: enabled },
    });
    try {
      const { accountApi } = await import('../../api/account.js');
      await accountApi.updatePreferences({ autoDiagnoseEnabled: enabled });
    } catch (err) {
      set({ autoDiagnoseEnabled: prevEnabled, diagnosePreferences: prevPrefs });
      throw err;
    }
  },

  updateAutoDiagnoseInterval: async (intervalHours) => {
    const prevInterval = get().autoDiagnoseIntervalHours;
    const prevPrefs = get().diagnosePreferences;
    set({
      autoDiagnoseIntervalHours: intervalHours,
      diagnosePreferences: { ...prevPrefs, autoDiagnoseIntervalHours: intervalHours },
    });
    try {
      const { accountApi } = await import('../../api/account.js');
      await accountApi.updatePreferences({ autoDiagnoseIntervalHours: intervalHours });
    } catch (err) {
      set({ autoDiagnoseIntervalHours: prevInterval, diagnosePreferences: prevPrefs });
      throw err;
    }
  },

  deductLocally: (points) => {
    const current = get().balance;
    if (current !== null) {
      set({ balance: Math.max(0, current - points) });
    }
  },

  clearAccount: () => {
    set({
      balance: null,
      balanceLoading: false,
      creditsHistory: [],
      creditsHistoryTotal: 0,
      subscription: null,
      invite: null,
      progressCloudSync: false,
      // 19BO+ B5.2-cron: reset auto-diagnose prefs to defaults on logout.
      autoDiagnoseEnabled: false,
      autoDiagnoseIntervalHours: 6,
    });
  },
});
